import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Flex, Text, Spinner } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import JobApplicationSteps from "./JobApplicationSteps";
import AgencyShortlist from "./Shortlist/AgencyShortlist";
import {
  candidateTabSuggested,
  candidateTabInterested,
  candidateTabShortlisted,
  candidateTabApproved,
  candidateTabHired,
  candidateTabRejected,
} from "../constants/candidateFlow";
import {
  AGENCY_JOB_CANDIDATE_TRIAGE_QUERY,
  AGENCY_JOB_CANDIDATE_URLS_QUERY,
} from "../graphql/jobs";
import { candidateMatchesDeepLinkId } from "../helpers/utils";

const SIGNED_URL_REFRESH_MS = 5 * 60 * 1000;

const URL_LIST_KEYS = [
  "agencyCandidateTriageSuggestedCandidates",
  "agencyCandidateTriageInterestedCandidates",
  "agencyCandidateTriageShortlistedCandidates",
  "agencyCandidateTriageApprovedCandidates",
  "agencyCandidateTriageHiredCandidates",
  "agencyCandidateTriageRejectedCandidates",
];

const listParamToTab = {
  suggested: candidateTabSuggested,
  interested: candidateTabInterested,
  shortlisted: candidateTabShortlisted,
  approved: candidateTabApproved,
  hired: candidateTabHired,
  rejected: candidateTabRejected,
};

function buildUrlsByProfileId(urlsData) {
  const map = new Map();
  if (!urlsData) return map;
  for (const key of URL_LIST_KEYS) {
    const list = urlsData[key];
    if (!list) continue;
    for (const report of list) {
      const cand = report?.candidate;
      if (cand?.candidateProfileId) map.set(cand.candidateProfileId, cand);
    }
  }
  return map;
}

// Enrichment merges the rotating signed `profileImageUrl` (fetched by a
// separate query) onto the cached `Candidate`. The triage query's `Candidate`
// objects are normalized by candidateProfileId, so an unchanged candidate keeps
// the same reference across polls. Spreading `{...cand}` here would throw that
// stability away and re-render every row + the chat drawer on every poll, so we
// cache enriched objects keyed by the source candidate reference (a WeakMap, so
// it clears automatically when the cache replaces the object) plus the current
// profileImageUrl. Same inputs => same output reference => no re-render.
const enrichedCandidateCache = new WeakMap();

function enrichCandidate(cand, urlsByProfileId) {
  if (!cand?.candidateProfileId) return cand;
  const urlCand = urlsByProfileId.get(cand.candidateProfileId);
  const profileImageUrl = urlCand?.profileImageUrl;
  if (profileImageUrl == null) return cand;
  const cached = enrichedCandidateCache.get(cand);
  if (cached && cached.profileImageUrl === profileImageUrl) {
    return cached.candidate;
  }
  const enriched = { ...cand, profileImageUrl };
  enrichedCandidateCache.set(cand, { profileImageUrl, candidate: enriched });
  return enriched;
}

// Cache enriched report wrappers the same way: if the report's candidate ref
// is unchanged and enrichment yields the same candidate reference, return the
// original report untouched so downstream memoization holds.
const enrichedReportCache = new WeakMap();

function enrichReport(report, urlsByProfileId) {
  if (!report?.candidate) return report;
  const enrichedCand = enrichCandidate(report.candidate, urlsByProfileId);
  if (enrichedCand === report.candidate) return report;
  const cached = enrichedReportCache.get(report);
  if (cached && cached.candidate === enrichedCand) {
    return cached.report;
  }
  const enriched = { ...report, candidate: enrichedCand };
  enrichedReportCache.set(report, { candidate: enrichedCand, report: enriched });
  return enriched;
}

// Cache the enriched array per source list so that when every report resolves
// to the same reference as last time, we return the *same array reference*.
// CandidateListItem's sort effect keys on this array; a fresh array every poll
// would re-fire it (and re-render the whole list) even though nothing changed.
const enrichedListCache = new WeakMap();

function enrichReportList(list, urlsByProfileId) {
  if (!list) return list;
  const enriched = list.map((report) => enrichReport(report, urlsByProfileId));
  const cached = enrichedListCache.get(list);
  if (
    cached &&
    cached.length === enriched.length &&
    cached.every((report, i) => report === enriched[i])
  ) {
    return cached;
  }
  enrichedListCache.set(list, enriched);
  return enriched;
}

export default function AgencyJobCandidateLists({ moblyzeJob, currentTab, setCurrentTab, isArchived }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showCandidateId = searchParams.get("showCandidate");
  const listParam = searchParams.get("list");
  const lastHandledCandidateRef = useRef(null);

  const { loading, error, data, refetch, startPolling, stopPolling } = useQuery(
    AGENCY_JOB_CANDIDATE_TRIAGE_QUERY,
    {
      variables: { id: moblyzeJob?.id },
      fetchPolicy: "cache-and-network",
      skip: !moblyzeJob?.id,
      notifyOnNetworkStatusChange: false,
    }
  );

  const { data: urlsData, refetch: refetchUrls } = useQuery(AGENCY_JOB_CANDIDATE_URLS_QUERY, {
    variables: { id: moblyzeJob?.id },
    skip: !moblyzeJob?.id,
    fetchPolicy: "cache-first",
    pollInterval: SIGNED_URL_REFRESH_MS,
  });

  const urlsByProfileId = useMemo(() => buildUrlsByProfileId(urlsData), [urlsData]);

  const candidateIdsKey = useMemo(() => {
    if (!data) return "";
    const ids = new Set();
    for (const key of URL_LIST_KEYS) {
      for (const report of data[key] || []) {
        if (report?.candidate?.candidateProfileId) ids.add(report.candidate.candidateProfileId);
      }
    }
    return [...ids].sort().join(",");
  }, [data]);

  // Refetch signed URLs whenever the set of candidates in the triage lists changes
  // so newly-appearing candidates get their URLs without waiting for the 5-min cycle.
  useEffect(() => {
    if (!candidateIdsKey) return;
    if (!urlsData) return;
    const cached = new Set(urlsByProfileId.keys());
    const current = candidateIdsKey.split(",").filter(Boolean);
    const hasMissing = current.some((id) => !cached.has(id));
    if (hasMissing) refetchUrls();
  }, [candidateIdsKey, urlsByProfileId, urlsData, refetchUrls]);

  const matchingProgress = data?.moblyzeJob?.matchingProgress;
  const isMatching = typeof matchingProgress === "number" && matchingProgress < 1;

  useEffect(() => {
    // Keep polling forever so we pick up re-matching triggered by criteria edits.
    // Poll fast while matching is in progress; slow-poll (once per minute)
    // once it settles.
    startPolling(isMatching ? 3000 : 60000);
    return () => stopPolling();
  }, [isMatching, startPolling, stopPolling]);

  const enrichedLists = useMemo(() => {
    if (!data) return null;
    const out = {};
    for (const key of URL_LIST_KEYS) {
      out[key] = enrichReportList(data[key], urlsByProfileId);
    }
    return out;
  }, [data, urlsByProfileId]);

  useEffect(() => {
    if (!data) {
      return;
    }
    // When a candidate is referenced, its containing tab wins over an
    // explicit ?list= param so deep links land on the right candidate.
    if (showCandidateId) {
      if (lastHandledCandidateRef.current === showCandidateId) {
        return;
      }
      const tabsWithLists = [
        [candidateTabSuggested, data.agencyCandidateTriageSuggestedCandidates],
        [candidateTabInterested, data.agencyCandidateTriageInterestedCandidates],
        [candidateTabShortlisted, data.agencyCandidateTriageShortlistedCandidates],
        [candidateTabApproved, data.agencyCandidateTriageApprovedCandidates],
        [candidateTabHired, data.agencyCandidateTriageHiredCandidates],
        [candidateTabRejected, data.agencyCandidateTriageRejectedCandidates],
      ];
      for (const [tab, list] of tabsWithLists) {
        if (list?.some((cr) => candidateMatchesDeepLinkId(cr?.candidate, showCandidateId))) {
          if (currentTab !== tab) {
            setCurrentTab(tab);
          }
          lastHandledCandidateRef.current = showCandidateId;
          return;
        }
      }
      // Candidate id didn't match any list — fall through to the list param.
      lastHandledCandidateRef.current = showCandidateId;
    }

    const tabFromList = listParam && listParamToTab[listParam.toLowerCase()];
    if (tabFromList && currentTab !== tabFromList) {
      setCurrentTab(tabFromList);
    }
  }, [listParam, showCandidateId, data, currentTab, setCurrentTab]);

  const handleTabChange = useCallback(
    (nextTab) => {
      setCurrentTab(nextTab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("list", String(nextTab).toLowerCase());
          next.delete("showCandidate");
          next.delete("tab");
          next.delete("chat");
          return next;
        },
        { replace: true }
      );
    },
    [setCurrentTab, setSearchParams]
  );

  if (loading && !data) {
    return (
      <Flex align="center" justify="center" minH="200px">
        <Spinner size="lg" />
      </Flex>
    );
  }
  if (error || !data) {
    return (
      <Text color="red.500">Failed to load candidate data.</Text>
    );
  }

  const steps = [
    { label: candidateTabSuggested, count: enrichedLists.agencyCandidateTriageSuggestedCandidates?.length || 0 },
    { label: candidateTabInterested, count: enrichedLists.agencyCandidateTriageInterestedCandidates?.length || 0 },
    { label: candidateTabShortlisted, count: enrichedLists.agencyCandidateTriageShortlistedCandidates?.length || 0 },
    { label: candidateTabApproved, count: enrichedLists.agencyCandidateTriageApprovedCandidates?.length || 0 },
    { label: candidateTabHired, count: enrichedLists.agencyCandidateTriageHiredCandidates?.length || 0 },
    { label: candidateTabRejected, count: enrichedLists.agencyCandidateTriageRejectedCandidates?.length || 0, dimmed: true },
  ];

  return (
    <>
      <JobApplicationSteps
        steps={steps}
        activeStep={currentTab}
        onTabChange={handleTabChange}
      />
      <AgencyShortlist
        moblyzeJob={moblyzeJob}
        matchingProgress={matchingProgress}
        agencyCandidateTriageSuggestedCandidates={enrichedLists.agencyCandidateTriageSuggestedCandidates}
        agencyCandidateTriageInterestedCandidates={enrichedLists.agencyCandidateTriageInterestedCandidates}
        agencyCandidateTriageShortlistedCandidates={enrichedLists.agencyCandidateTriageShortlistedCandidates}
        agencyCandidateTriageApprovedCandidates={enrichedLists.agencyCandidateTriageApprovedCandidates}
        agencyCandidateTriageHiredCandidates={enrichedLists.agencyCandidateTriageHiredCandidates}
        agencyCandidateTriageRejectedCandidates={enrichedLists.agencyCandidateTriageRejectedCandidates}
        selectedTab={currentTab}
        isArchived={isArchived}
        refetch={refetch}
      />
    </>
  );
}
